'use client';

import useScrollReveal from '@/hooks/useScrollReveal';

interface Principle {
  title: string;
  description: string;
  icon: string;
  number: string;
}

export default function OurApproach() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();
  
  const steps = [
    {
      title: 'Foundation Before Scale',
      description: 'I\'ve watched too many founders burn money trying to scale broken systems. You can\'t grow what doesn\'t work. We build the foundation first—positioning, messaging, infrastructure. Then we scale it.',
      number: '01'
    },
    {
      title: 'Infrastructure Before Traffic',
      description: "Driving people to an unprofessional website is like inviting customers to a half-built storefront. Waste of money. We make sure you\'re ready to convert before we send anyone your way.",
      number: '02'
    },
    {
      title: 'Clarity Before Growth',
      description: "If you can\'t explain what you do in two sentences, your marketing won\'t work. We start with positioning—who you serve, what problem you solve, why you\'re different. Everything else flows from there.",
      number: '03'
    },
  ];

  return (
    <section
      id="approach"
      ref={sectionRef}
      className={`py-12 sm:py-16 md:py-24 lg:py-32 bg-white transition-all duration-1000 ${
        sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl text-brand-plum mb-3 sm:mb-4 font-bold leading-tight">
            How We Work
          </h2>
          <p className="font-body text-sm sm:text-base md:text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
            Three principles that guide every project we take on.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {steps.map((step, index) => {
            const { ref: cardRef, isVisible: cardVisible } = useScrollReveal<HTMLDivElement>();
            return (
              <div
                key={index}
                ref={cardRef}
                className={`group relative bg-white p-4 sm:p-6 border-l-4 border-brand-plum hover:border-brand-teal transition-all duration-500 transform hover:-translate-y-2 hover:shadow-2xl shadow-lg ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
              {/* Number and Icon */}
              <div className="mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-brand-teal text-white text-base sm:text-lg font-headline font-bold transform group-hover:scale-110 transition-transform duration-300">
                  {step.number}
                </div>
                <span className="text-3xl sm:text-4xl transform group-hover:scale-110 transition-transform duration-300">{step.icon}</span>
              </div>
              
              <h3 className="font-headline text-lg sm:text-xl text-brand-plum mb-2 sm:mb-3 font-bold group-hover:text-brand-teal transition-colors duration-300">
                {step.title}
              </h3>
              <p className="font-body text-xs sm:text-sm text-text-secondary leading-relaxed">
                {step.description}
              </p>
              
              {/* Bottom Accent */}
              <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full bg-brand-teal transition-all duration-500"></div>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}